/* src/map/tilePipeline.ts — orchestrates the decode → build worker chain.
 *
 * The main thread's entire involvement in tile loading is: pick a worker, post a request, and
 * relay one transferable payload from the decode worker to the geometry worker. It never
 * reads a coordinate. PLAN.md Part 2 §3 explicitly sanctions main-thread relay as an
 * alternative to a worker-to-worker MessageChannel; relaying is chosen here because it is
 * zero-copy either way and has far fewer moving parts.
 */

import type {
  BuildRequest,
  DecodeRequest,
  DecodeWorkerOut,
  GeometryWorkerOut,
  PackedMesh,
  PackedPaths,
} from '../workers/protocol.ts';
import { layerTransferables } from '../workers/protocol.ts';

export interface TileResult {
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly meshes: PackedMesh[];
  readonly triangles: number;
  readonly bytes: number;
  /** The mercator→metre scale this tile's geometry was built with (cos of the origin lat). */
  readonly metreScale: number;
  /** Road centrelines for the ambient sim; absent when the tile has no roads. */
  readonly paths?: PackedPaths;
}

export interface TileFailure {
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly message: string;
  /** True when the tile is known-absent rather than broken — do not retry these. */
  readonly permanent: boolean;
}

type Resolve = (result: TileResult) => void;
type Reject = (failure: TileFailure) => void;

interface Pending {
  readonly z: number;
  readonly x: number;
  readonly y: number;
  readonly metreScale: number;
  readonly resolve: Resolve;
  readonly reject: Reject;
  bytes: number;
  decoderSlot: number;
  builderSlot: number;
}

/**
 * Pool size. Two of each keeps a fast pan fed without oversubscribing a mid-range laptop —
 * more decoders than cores just adds context switching to a job that is already IO-bound at
 * the fetch and CPU-bound at the parse.
 */
const POOL_SIZE = 2;

/*
 * Workers are RECYCLED, not kept for the session. V8 grows a worker's heap to fit its
 * busiest moment and rarely hands the memory back to the OS — after a few dense cities each
 * build worker idles at hundreds of megabytes that no page-side heap gauge can even see.
 * Terminating an idle worker after a batch of jobs releases its whole heap; a fresh module
 * worker warms up in tens of milliseconds. Builders churn the most garbage per job, so they
 * recycle sooner.
 */
const DECODER_JOB_LIMIT = 160;
const BUILDER_JOB_LIMIT = 80;

interface Slot {
  worker: Worker;
  /** Requests posted but not yet answered — never terminate under these. */
  outstanding: number;
  /** Jobs answered since this worker was (re)spawned. */
  jobs: number;
}

export class TilePipeline {
  readonly #decoders: Slot[] = [];
  readonly #builders: Slot[] = [];
  readonly #pending = new Map<string, Pending>();
  #nextId = 0;
  #rr = 0;

  constructor() {
    for (let i = 0; i < POOL_SIZE; i += 1) {
      this.#decoders.push(this.#spawnDecoder());
      this.#builders.push(this.#spawnBuilder());
    }
  }

  #spawnDecoder(): Slot {
    const worker = new Worker(new URL('../workers/vectorTileWorker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<DecodeWorkerOut>) => {
      this.#onDecoded(e.data);
    };
    return { worker, outstanding: 0, jobs: 0 };
  }

  #spawnBuilder(): Slot {
    const worker = new Worker(new URL('../workers/geometryWorker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<GeometryWorkerOut>) => {
      this.#onBuilt(e.data);
    };
    return { worker, outstanding: 0, jobs: 0 };
  }

  /** Book a finished job against a slot, and recycle the worker once it is idle and stale. */
  #settle(slots: Slot[], index: number, limit: number, spawn: () => Slot): void {
    const slot = slots[index];
    if (!slot) return;
    slot.outstanding -= 1;
    slot.jobs += 1;
    if (slot.outstanding === 0 && slot.jobs >= limit) {
      slot.worker.terminate();
      slots[index] = spawn();
    }
  }

  get inFlight(): number {
    return this.#pending.size;
  }

  /**
   * `metreScale` is per-request rather than per-pipeline: it is cos(lat) at the *current*
   * floating origin, and the origin moves across the whole country now. Tiles remember the
   * scale they were built with; the tile manager corrects the residual ratio with a group
   * scale, so tiles built before and after a long journey still meet edge-to-edge.
   */
  load(z: number, x: number, y: number, url: string, metreScale: number): Promise<TileResult> {
    return new Promise<TileResult>((resolve, reject) => {
      const id = `t${String(this.#nextId++)}`;
      const decoderSlot = this.#rr++ % POOL_SIZE;
      this.#pending.set(id, {
        z, x, y, metreScale, resolve, reject, bytes: 0, decoderSlot, builderSlot: -1,
      });

      const slot = this.#decoders[decoderSlot];
      if (!slot) return;
      slot.outstanding += 1;
      const req: DecodeRequest = { kind: 'decode', id, z, x, y, url };
      slot.worker.postMessage(req);
    });
  }

  #onDecoded(msg: DecodeWorkerOut): void {
    const pending = this.#pending.get(msg.id);
    if (!pending) return;
    this.#settle(this.#decoders, pending.decoderSlot, DECODER_JOB_LIMIT, () =>
      this.#spawnDecoder(),
    );

    if (msg.kind === 'decode-error') {
      this.#pending.delete(msg.id);
      pending.reject({
        z: pending.z, x: pending.x, y: pending.y,
        message: msg.message,
        // 404 means the tile genuinely has nothing. Anything else might succeed on a retry.
        permanent: msg.status === 404,
      });
      return;
    }

    pending.bytes = msg.bytes;

    // An empty tile is a normal outcome at the edge of the ingested area — resolve with no
    // meshes rather than sending an empty job through the geometry worker.
    if (Object.keys(msg.layers).length === 0) {
      this.#pending.delete(msg.id);
      pending.resolve({
        z: msg.z, x: msg.x, y: msg.y, meshes: [], triangles: 0, bytes: msg.bytes,
        metreScale: pending.metreScale,
      });
      return;
    }

    const builderSlot = this.#rr++ % POOL_SIZE;
    pending.builderSlot = builderSlot;
    const slot = this.#builders[builderSlot];
    if (!slot) return;
    slot.outstanding += 1;
    const req: BuildRequest = {
      kind: 'build',
      id: msg.id,
      z: msg.z,
      x: msg.x,
      y: msg.y,
      layers: msg.layers,
      metreScale: pending.metreScale,
    };
    // The transfer list is what keeps this hop free: the buffers move, they are not copied,
    // and this thread's views are neutered immediately after the call.
    slot.worker.postMessage(req, layerTransferables(msg.layers));
  }

  #onBuilt(msg: GeometryWorkerOut): void {
    const pending = this.#pending.get(msg.id);
    if (!pending) return;
    this.#pending.delete(msg.id);
    this.#settle(this.#builders, pending.builderSlot, BUILDER_JOB_LIMIT, () =>
      this.#spawnBuilder(),
    );

    if (msg.kind === 'build-error') {
      pending.reject({
        z: pending.z, x: pending.x, y: pending.y, message: msg.message, permanent: false,
      });
      return;
    }

    pending.resolve({
      z: msg.z, x: msg.x, y: msg.y,
      meshes: msg.meshes, triangles: msg.triangles, bytes: pending.bytes,
      metreScale: pending.metreScale,
      ...(msg.paths ? { paths: msg.paths } : {}),
    });
  }

  dispose(): void {
    for (const s of this.#decoders) s.worker.terminate();
    for (const s of this.#builders) s.worker.terminate();
    this.#decoders.length = 0;
    this.#builders.length = 0;
    this.#pending.clear();
  }
}
